import Phaser from "phaser";
import type { PuzzleEvent, PuzzleMatchState } from "../simulation";
import { PuzzleSceneBridge } from "./sceneBridge";
import {
  PUZZLE_RENDER_HEIGHT,
  PUZZLE_RENDER_WIDTH,
  PUZZLE_PORTRAIT_RENDER_HEIGHT,
  PUZZLE_PORTRAIT_RENDER_WIDTH,
  PuzzleScene,
  puzzleAimGeometry,
  type PuzzleAimGeometry,
  type PuzzleFocusRef
} from "./scenes/PuzzleScene";

export interface PuzzleRendererOptions {
  parent: HTMLElement;
  /** Non-consuming aim value used only for visual trajectory feedback. */
  readAim?: () => number;
  /** Board to promote as the central player board. Defaults to the first human. */
  playerId?: string;
  /** Multi-board spectator layout for small admin cards. */
  compact?: boolean;
  /** Portrait player layout, shared by local and remote matches. */
  portrait?: boolean;
  /** Local landscape 1:1 renderer with equal player-left and AI-right boards. */
  duelComparison?: boolean;
}

/** Disposable Phaser adapter; all authoritative rules stay in simulation. */
export class PuzzleRenderer {
  readonly bridge = new PuzzleSceneBridge();
  readonly game: Phaser.Game;
  private readonly focus: PuzzleFocusRef;
  private readonly scene: PuzzleScene;
  readonly aimGeometry: PuzzleAimGeometry;
  private destroyed = false;

  constructor(options: PuzzleRendererOptions) {
    const portrait = !options.compact && (options.portrait ?? false);
    const duelComparison = !options.compact && !portrait && (options.duelComparison ?? false);
    const width = portrait ? PUZZLE_PORTRAIT_RENDER_WIDTH : PUZZLE_RENDER_WIDTH;
    const height = portrait ? PUZZLE_PORTRAIT_RENDER_HEIGHT : PUZZLE_RENDER_HEIGHT;
    this.aimGeometry = puzzleAimGeometry(portrait, duelComparison);
    this.focus = {};
    if (options.playerId) this.focus.playerId = options.playerId;
    this.scene = new PuzzleScene({
      bridge: this.bridge,
      focus: this.focus,
      readAim: options.readAim ?? (() => 0),
      compact: options.compact ?? false,
      portrait,
      duelComparison
    });
    this.game = new Phaser.Game({
      // Compact spectator cards may exist by the dozen. Canvas keeps every
      // feed alive without consuming one WebGL context per match.
      type: options.compact ? Phaser.CANVAS : Phaser.AUTO,
      parent: options.parent,
      width,
      height,
      backgroundColor: "#030a12",
      scene: this.scene,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width,
        height
      },
      render: {
        antialias: true,
        pixelArt: false,
        roundPixels: true
      },
      input: {
        activePointers: 3
      },
      fps: {
        target: 60,
        smoothStep: true
      }
    });
  }

  publish(state: PuzzleMatchState): void {
    if (!this.destroyed) this.bridge.publishSnapshot(state);
  }

  publishEvents(events: PuzzleEvent[]): void {
    if (!this.destroyed) this.bridge.publishEvents(events);
  }

  setPlayerId(playerId?: string): void {
    if (playerId) this.focus.playerId = playerId;
    else delete this.focus.playerId;
    this.scene.refreshFocus();
  }

  setPaused(paused: boolean): void {
    if (this.destroyed) return;
    if (paused) this.game.loop.sleep();
    else this.game.loop.wake();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.game.destroy(true);
    this.bridge.clear();
  }
}

export {
  PUZZLE_PORTRAIT_RENDER_HEIGHT,
  PUZZLE_PORTRAIT_RENDER_WIDTH,
  PUZZLE_RENDER_HEIGHT,
  PUZZLE_RENDER_WIDTH
} from "./scenes/PuzzleScene";
export { toPuzzleViewModel } from "./viewModel";
